import React, { useState } from "react";
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PrintIcon from "@mui/icons-material/Print";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { alpha } from "@mui/material/styles";
import CustomDialog from "../utils/dialog/customDialog";
import BaseHeader from "../Common/BaseHeader";
import HiddenButton from "../utils/button/HiddenButton";
import SavyButton from "../utils/button/SavyButton";

const IconGroup = ({
  icon: Icon,
  title,
  description,
  iconColor,
  iconBackground,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
          bgcolor: iconBackground
            ? iconBackground
            : (theme) => alpha(theme.palette.warning.main, 0.1),
        }}
      >
        <Icon color={iconColor ? iconColor : "warning"} fontSize='small' />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const ScheduleHeader = ({
  title,
  companyName,
  companyType = "common",
  uen,
  yearOfAssessment,
  financialYearStart,
  financialYearEnd,
  currency,
  handleSpreadSheetPrint,
  deleteSchedule,
  handleSave,
  handleExcel,
  handleManageAdjustments,
  handleTool,
  toolName,
  children,
  sticky = true,
  canEditSchedule = true,
  onSettingsClick,
  showSettings = false,
  hasActiveSettings = false,
  onHelpClick,
  helpLabel = "Column guide",
  canUseAi = false,
  renderAiType = "",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSticky, setIsSticky] = useState(sticky);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    if (deleteSchedule) deleteSchedule();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const actions = (
    <>
      {canEditSchedule && handleTool && (
        <Button
          variant='outlined'
          color='primary'
          onClick={handleTool}
          startIcon={<CalculateIcon />}
          sx={{
            textTransform: "none",
            fontSize: "0.85rem",
            padding: "4px 10px",
            minHeight: "32px",
          }}
        >
          {toolName}
        </Button>
      )}
      {canEditSchedule && handleExcel && (
        <Button
          variant='outlined'
          color='info'
          onClick={handleExcel}
          startIcon={<UploadFileIcon />}
          sx={{
            textTransform: "none",
            fontSize: "0.85rem",
            padding: "4px 10px",
            minHeight: "32px",
          }}
        >
          Upload Excel
        </Button>
      )}
      {canEditSchedule && handleManageAdjustments && (
        <Button
          variant='outlined'
          color='primary'
          onClick={handleManageAdjustments}
          startIcon={<TuneIcon />}
          sx={{
            textTransform: "none",
            fontSize: "0.85rem",
            padding: "4px 10px",
            minHeight: "32px",
          }}
        >
          Manage Adjustments
        </Button>
      )}
      {canEditSchedule && handleSave && (
        <SavyButton
          variant='contained'
          color='success'
          onClick={() => handleSave()}
          startIcon={<SaveIcon />}
        >
          Save
        </SavyButton>
      )}
      {canEditSchedule && showSettings && onSettingsClick && (
        <Tooltip title='Schedule Settings'>
          <IconButton
            onClick={onSettingsClick}
            sx={{
              backgroundColor: "#F5F5F5",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": { backgroundColor: "#E0E0E0" },
              position: "relative",
            }}
          >
            <SettingsIcon sx={{ color: "#424242" }} />
            {hasActiveSettings && (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#4CAF50",
                  border: "1px solid white",
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      )}
      {onHelpClick && (
        <HiddenButton
          icon={<HelpOutlineIcon />}
          label={helpLabel}
          onClick={onHelpClick}
          aria-label={helpLabel}
          aria-haspopup='dialog'
          variant='outlined'
          color='primary'
          expandedWidth={148}
        />
      )}
      <IconButton
        onClick={handleMenuOpen}
        sx={{
          backgroundColor: "#F5F5F5",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          "&:hover": { backgroundColor: "#E0E0E0" },
        }}
      >
        <MoreVertIcon sx={{ color: "#424242" }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: 2,
            boxShadow: (theme) =>
              `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
            minWidth: 280,
            "& .MuiList-root": {
              py: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
            Schedule Options
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <InfoOutlinedIcon fontSize='small' />
            Available actions for this schedule
          </Typography>
        </Box>
        <Box sx={{ px: 1, py: 0.5 }}>
          {children && <Box>{children}</Box>}
          {handleSpreadSheetPrint && (
            <>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  mb: 1,
                }}
              >
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 600,
                    color: "info.main",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  Spreadsheet Options
                </Typography>
              </Box>
              <MenuItem
                onClick={handleSpreadSheetPrint}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  py: 1.5,
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1,
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
                    }}
                  >
                    <PrintIcon color='info' fontSize='small' />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      Print Spreadsheet
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Print the spreadsheet for this schedule
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </>
          )}
          {canEditSchedule && deleteSchedule && (
            <>
              <Divider sx={{ my: 1 }} />
              <MenuItem
                onClick={handleDeleteClick}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  py: 1.5,
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <IconGroup
                  icon={DeleteForeverIcon}
                  title='Delete Schedule'
                  description='Deletes the schedule permanently'
                  iconColor='error'
                  iconBackground={(theme) =>
                    alpha(theme.palette.error.main, 0.21)
                  }
                />
              </MenuItem>
            </>
          )}
        </Box>
      </Menu>
      <CustomDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title='Delete Schedule'
        description='Are you sure you want to delete this schedule? This action cannot be undone.'
        type='error'
      />
    </>
  );

  return (
    <BaseHeader
      title={title}
      companyName={companyName}
      companyType={companyType}
      uen={uen}
      yearOfAssessment={yearOfAssessment}
      financialYearStart={financialYearStart}
      financialYearEnd={financialYearEnd}
      currency={currency}
      actions={actions}
      sticky={isSticky}
      aiSettings={{ canUseAi, renderAiType }}
    />
  );
};

export default React.memo(ScheduleHeader);
